/**
 * @file crm-customer-view.ts
 * @description Как показать клиента в кабинете магазина (phase19.31, зона
 * «Клиенты и сделки»; SC43, SC63): вид клиента словом, имя, пометки «блок» и
 * «риск», тон стадии и оплаты в списке заказов клиента.
 *
 * Чистые функции и реестры — одни и те же в реестре клиентов и в карточке.
 */

import type { CustomerDto, CustomerOrderListItemDto, CustomerType } from "./api-customers";
import { pluralRu } from "./plural";
import { getShopTimeZone } from "./shop-time";
import type { StatusTone } from "./shop/status-meta";

/** Календарный день «гггг-мм-дд» по поясу магазина (SC31). */
function shopDayKey(ms: number): string {
  return new Date(ms).toLocaleDateString("en-CA", { timeZone: getShopTimeZone() });
}

/**
 * Сколько прошло с даты — для пояснения плитки «Последний заказ»:
 * «сегодня», «вчера», «10 дней назад», от 60 дней — «3 мес. назад».
 * Считаются календарные дни магазина, а не сутки от текущего часа.
 */
export function daysAgoText(iso: string, now: number = Date.now()): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const days = Math.round((Date.parse(shopDayKey(now)) - Date.parse(shopDayKey(then))) / 86_400_000);
  if (days <= 0) return "сегодня";
  if (days === 1) return "вчера";
  if (days < 60) return `${days} ${pluralRu(days, ["день", "дня", "дней"])} назад`;
  return `${Math.floor(days / 30)} мес. назад`;
}

/** Вид клиента словом (без точки «Юр.лицо»). */
export const CUSTOMER_TYPE_LABEL: Record<CustomerType, string> = {
  INDIVIDUAL: "Физлицо",
  LEGAL: "Юрлицо",
};

/** Подпись клиента: имя, иначе почта, иначе телефон. */
export function customerTitle(c: Pick<CustomerDto, "displayName" | "email" | "phone">): string {
  return c.displayName?.trim() || c.email || c.phone || "Без имени";
}

/** Порог риск-скора, с которого клиент получает пометку «риск». */
export const CUSTOMER_RISK_THRESHOLD = 50;

/** Блокировки словами: «заказы», «вход», «оплата». */
const BLOCK_SCOPE_LABEL: Record<string, string> = {
  ORDERS: "заказы",
  LOGIN: "вход",
  PAYMENT: "оплата",
};

export interface CustomerMark {
  key: string;
  label: string;
  tone: StatusTone;
}

/** Пометки строки реестра: блокировка, риск, анонимизирован, неактивен. */
export function customerMarks(
  c: Pick<CustomerDto, "blockedScopes" | "riskScore" | "anonymizedAt" | "isActive">,
): CustomerMark[] {
  const marks: CustomerMark[] = [];
  if (c.blockedScopes.length > 0) {
    const scopes = c.blockedScopes.map((s) => BLOCK_SCOPE_LABEL[s] ?? s.toLowerCase()).join(", ");
    marks.push({ key: "block", label: `Блок: ${scopes}`, tone: "error" });
  }
  if (c.riskScore > CUSTOMER_RISK_THRESHOLD) marks.push({ key: "risk", label: `Риск ${c.riskScore}`, tone: "attention" });
  if (c.anonymizedAt) marks.push({ key: "anon", label: "Анонимизирован", tone: "neutral" });
  else if (!c.isActive) marks.push({ key: "inactive", label: "Неактивен", tone: "neutral" });
  return marks;
}

/**
 * Тон стадии заказа в списке заказов клиента. Сервер отдаёт только название
 * стадии (без вида и цвета), поэтому тон выводим из того, что есть: отменён —
 * красный, оплачен и отгружен целиком — зелёный, остальное — «в работе».
 * Когда сервер начнёт отдавать вид стадии, тон станет по виду (как в реестре
 * заказов).
 */
export function customerOrderTone(
  o: Pick<CustomerOrderListItemDto, "isCancelled" | "paymentStatus" | "shipmentStatus">,
): StatusTone {
  if (o.isCancelled) return "error";
  if (o.paymentStatus === "FULL" && o.shipmentStatus === "FULL") return "success";
  return "progress";
}

/** Оплата заказа словом и тоном текста. */
export const CUSTOMER_ORDER_PAYMENT: Record<CustomerOrderListItemDto["paymentStatus"], { label: string; tone: StatusTone }> = {
  NONE: { label: "Не оплачен", tone: "neutral" },
  PARTIAL: { label: "Оплачен частично", tone: "attention" },
  FULL: { label: "Оплачен", tone: "success" },
  REFUNDED: { label: "Возврат", tone: "error" },
};
